import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { downloadBlob } from 'src/utils/format';
import {
    exportReferralSubs,
    exportReferralUsers,
    getReferralDetail,
    getReferralKpi,
    getReferralUsers,
} from '../services/dashboardV3.service';
import type {
    MethodFilter,
    RangeValue,
    ReferralKpiData,
    ReferralMeta,
    ReferralProductType,
    ReferralRow,
    SortOrder,
    UserTypeFilter,
} from '../services/dashboardV3.types';

export interface UseDashboardV3ReferralParams {
    range: RangeValue;
    startDate?: string;
    endDate?: string;
    productType: ReferralProductType;

    page: number;
    size: number;
    loginMethod: MethodFilter;
    userType: UserTypeFilter;
    sortBy: string;
    sortOrder: SortOrder;
    detailId?: string;

    userId?: string;
    walletAddress?: string;
    invitationCode?: string;
    loginAccount?: string;
}

export function useDashboardV3ReferralData(params: UseDashboardV3ReferralParams) {
    const notify = useNotify();

    const [kpi, setKpi] = useState<ReferralKpiData | null>(null);
    const [list, setList] = useState<ReferralRow[]>([]);
    const [total, setTotal] = useState(0);
    const [detail, setDetail] = useState<ReferralMeta | null>(null);

    const [kpiLoading, setKpiLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    /**
     * 只给 /referral/kpi 使用。
     * 只有首页 KPI 卡片需要根据时间范围动态变化。
     */
    const kpiParams = useMemo(
        () => ({
            range: params.range,
            startDate: params.range === 'custom' ? params.startDate : undefined,
            endDate: params.range === 'custom' ? params.endDate : undefined,
            productType: params.productType,
        }),
        [params.range, params.startDate, params.endDate, params.productType]
    );


    /**
     * 只给 /referral/users 和 /referral/users/export 使用。
     * 注意：这里不要带 range / startDate / endDate。
     *
     * 这样 time range 更新时，userParams 不变，
     * getReferralUsers 不会重新请求。
     */
    const userParams = useMemo(
        () => ({
            page: params.page,
            size: params.size,
            loginMethod: params.loginMethod,
            userType: params.userType,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            productType: params.productType,

            userId: params.userId,
            walletAddress: params.walletAddress,
            invitationCode: params.invitationCode,
            loginAccount: params.loginAccount,
        }),
        [
            params.page,
            params.size,
            params.loginMethod,
            params.userType,
            params.sortBy,
            params.sortOrder,
            params.productType,
            params.userId,
            params.walletAddress,
            params.invitationCode,
            params.loginAccount,
        ]
    );

    /**
     * 首页 KPI：受时间范围影响。
     */
    useEffect(() => {
        let alive = true;

        setKpiLoading(true);

        getReferralKpi(kpiParams)
            .then((kpiResp) => {
                if (!alive) return;
                setKpi(kpiResp.data);
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(e?.message ?? '获取邀请统计数据失败', { type: 'warning' });
                setKpi(null);
            })
            .finally(() => {
                if (!alive) return;
                setKpiLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [kpiParams, notify]);

    /**
     * 邀请主列表：不受时间范围影响。
     * 只有 page / size / loginMethod / userType / sortBy / sortOrder 变化时才刷新。
     */
    useEffect(() => {
        let alive = true;

        setUsersLoading(true);

        getReferralUsers(userParams)
            .then((usersResp) => {
                if (!alive) return;
                setList(usersResp.data?.list ?? []);
                setTotal(usersResp.total ?? 0);
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(e?.message ?? '获取邀请用户列表失败', { type: 'warning' });
                setList([]);
                setTotal(0);
            })
            .finally(() => {
                if (!alive) return;
                setUsersLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [userParams, notify]);

    /**
     * Detail KPI 卡片 + Detail 下级列表：不传时间范围。
     * 所以 time range 更新时，详情弹框也不会重新请求。
     */
    useEffect(() => {
        if (!params.detailId) {
            setDetail(null);
            return;
        }

        let alive = true;
        setDetailLoading(true);

        getReferralDetail(params.detailId, { productType: params.productType })
            .then((resp) => {
                if (!alive) return;
                setDetail(resp.data);
            })
            .catch((e: any) => {
                if (!alive) return;
                notify(e?.message ?? '获取下级详情失败', { type: 'warning' });
                setDetail(null);
            })
            .finally(() => {
                if (!alive) return;
                setDetailLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [params.detailId, params.productType, notify]);

    /**
     * 邀请主列表导出：不传时间范围。
     */
    const exportUsersCsv = useCallback(async () => {
        if (exporting) return;

        setExporting(true);

        try {
            const { blob, filename } = await exportReferralUsers({
                ...userParams,
                format: 'csv',
            });

            downloadBlob(filename, blob);
            notify('导出成功', { type: 'info' });
        } catch (e: any) {
            notify(e?.message ?? '导出失败', { type: 'warning' });
        } finally {
            setExporting(false);
        }
    }, [exporting, userParams, notify]);

    /**
     * Detail 下级列表导出：不传时间范围。
     */
    const exportSubsCsv = useCallback(
        async (idOrCode: string, filter: 'all' | 'direct' | 'team') => {
            if (exporting) return;

            setExporting(true);

            try {
                const { blob, filename } = await exportReferralSubs(idOrCode, {
                    filter,
                    format: 'csv',
                    productType: params.productType,
                });

                downloadBlob(filename, blob);
                notify('导出成功', { type: 'info' });
            } catch (e: any) {
                notify(e?.message ?? '导出失败', { type: 'warning' });
            } finally {
                setExporting(false);
            }
        },
        [exporting, notify, params.productType]
    );

    return {
        kpi,
        list,
        total,
        detail,
        loading: kpiLoading || usersLoading,
        kpiLoading,
        usersLoading,
        detailLoading,
        exporting,
        exportUsersCsv,
        exportSubsCsv,
    };
}